/**
 * Testing — UI acceptance tests of this application and its websites.
 *
 * A guided flow: test cases (made from the requirements and the screens) →
 * run them in a real browser → decide on the bugs they found. Overview answers
 * "is the app working?", Runs "what went wrong", Bugs "decide in one place",
 * Test cases are grouped by requirement in plain words. Test data a run
 * creates is kept and cleaned on demand. Unit tests run the transaction
 * designer's CRUD test per screen through the engine (no browser), results kept.
 * Plan: docs/plans/ui-acceptance-testing.md; doc: docs/ui_acceptance_testing.md.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, Play, RefreshCw, SlidersHorizontal, Sparkles } from 'lucide-react'
import Button from '@/components/common/Button'
import Spinner from '@/components/common/Spinner'
import { useProjectStore } from '@/store/projectStore'
import {
  apiError, getCoverage, getOverview, getRun, listUnitTests, seedCases, startRun,
  type CaseStatus, type Coverage, type Overview, type RunDetail,
} from '@/api/uiTesting'
import OverviewTab from './OverviewTab'
import CasesTab from './CasesTab'
import RunsTab from './RunsTab'
import DefectsTab from './DefectsTab'
import TestDataTab from './TestDataTab'
import UnitTestsTab from './UnitTestsTab'
import RunModal from './RunModal'
import { askAiToFixCase, askAiToFixUnitChecks, askAiToWriteTests, type UnitFixItem } from './shared'

export type TabKey = 'overview' | 'cases' | 'runs' | 'unit' | 'bugs' | 'data'

/** Where a tab should open: a run (and a case in it), or a requirement. */
export interface Focus { run?: string | null; caseId?: string | null; requirement?: string | null }

/** A case's most recent result across the recent runs. */
export interface LastResult { status: CaseStatus; at?: string; runId: string }

/** What every tab can ask the page to do. */
export interface TestingNav {
  go: (tab: TabKey, focus?: Focus) => void
  /** Start a run — of these cases, or of every case. */
  runCases: (caseIds?: string[]) => Promise<void>
  askAi: (focus?: string) => void
  /** Fix one failed case in place with Ask AI (developers / managers). */
  fixCase: (runId: string, caseId: string, title: string, failure: string, obj?: string) => void
}

const LIVE = new Set(['queued', 'running'])
/** How many recent runs are read to know each case's latest result. */
const RESULT_RUNS = 6

export default function TestingPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()
  const [tab, setTab] = useState<TabKey>('overview')
  const [focus, setFocus] = useState<Focus>({})
  const [overview, setOverview] = useState<Overview | null>(null)
  const [coverage, setCoverage] = useState<Coverage | null>(null)
  const [recent, setRecent] = useState<RunDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [chooseOpen, setChooseOpen] = useState(false)
  const [unitFailing, setUnitFailing] = useState(0)

  useEffect(() => { document.title = `Testing · ${project}` }, [project])

  const loadRecent = useCallback(async (ov: Overview) => {
    const ids = ov.runs.slice(0, RESULT_RUNS).map((r) => r.run_id)
    const got = await Promise.all(ids.map((id) => getRun(project, id).catch(() => null)))
    setRecent(got.filter(Boolean) as RunDetail[])
  }, [project])

  const reload = useCallback(async () => {
    if (!project) return
    try {
      const ov = await getOverview(project)
      setOverview(ov)
      const [cov, unit] = await Promise.all([
        getCoverage(project).catch(() => null),
        listUnitTests(project).catch(() => null),
        loadRecent(ov).catch(() => undefined),
      ])
      setCoverage(cov)
      setUnitFailing((unit?.objects || []).filter((o) => o.built && o.result && !o.result.report.ok).length)
    } catch (err) {
      toast.error(apiError(err, 'Could not load this application’s testing overview'))
    } finally {
      setLoading(false)
    }
  }, [project, loadRecent])

  useEffect(() => { void reload() }, [reload])

  // Each case's newest result — runs come newest first, so the first wins.
  const lastResults = useMemo(() => {
    const out: Record<string, LastResult> = {}
    for (const r of recent) {
      for (const c of r.cases || []) {
        if (out[c.id] || c.status === 'skipped') continue
        out[c.id] = { status: c.status, at: r.finished_at || r.started_at, runId: r.run_id }
      }
    }
    return out
  }, [recent])

  const canTest = !!overview?.can_test
  const openCount = overview?.defects?.open ?? 0
  const latestRun = recent[0] ?? null

  const go = useCallback((t: TabKey, f: Focus = {}) => {
    setFocus(f)
    setTab(t)
    window.scrollTo({ top: 0 })
  }, [])

  const runCases = useCallback(async (caseIds?: string[]) => {
    setBusy('run')
    try {
      const r = await startRun(project, { trace: 'failed', ...(caseIds?.length ? { case_ids: caseIds } : {}) })
      toast.success(`Running ${r.cases} test${r.cases === 1 ? '' : 's'}`
        + (r.skipped_invalid ? ` — ${r.skipped_invalid} skipped because they have errors` : ''))
      go('runs', { run: r.run_id })
      void reload()
    } catch (err) {
      toast.error(apiError(err, 'Could not start the run'))
    } finally {
      setBusy(null)
    }
  }, [project, go, reload])

  // The Ask AI panel serves the project open in the workbench, and drops a
  // request for any other — so say so rather than appear to have asked.
  const dockReady = useCallback(() => {
    if (useProjectStore.getState().currentProject?.application.folder_name !== project) {
      toast.error('Open this application in the workbench first — the Ask AI panel works on the open application')
      return false
    }
    return true
  }, [project])

  const askAi = useCallback((f?: string) => {
    if (!dockReady()) return
    askAiToWriteTests(project, f)
    toast('Asked AI to write the tests — follow it in the Ask AI panel', { icon: '✨' })
  }, [project, dockReady])

  const fixCase = useCallback((runId: string, caseId: string, title: string, failure: string,
                               obj?: string) => {
    if (!dockReady()) return
    askAiToFixCase(project, runId, caseId, title, failure, obj)
    toast('Asked AI to fix it and re-run the case — follow it in the Ask AI panel', { icon: '✨' })
  }, [project, dockReady])

  // Failed unit-test checks — one, or all of them — to the Ask-AI dock.
  const fixUnit = useCallback((items: UnitFixItem[]) => {
    if (!dockReady() || !items.length) return
    askAiToFixUnitChecks(project, items)
    toast(items.length === 1
      ? 'Asked AI to fix it and re-run the unit test — follow it in the Ask AI panel'
      : `Asked AI to fix the ${items.length} failures and re-run the unit tests — follow it in the Ask AI panel`,
    { icon: '✨' })
  }, [project, dockReady])

  const onSeed = async () => {
    setBusy('seed')
    try {
      const r = await seedCases(project)
      toast.success(`${r.total_seeded} generated cases — ${r.written.length} new or updated, `
        + `${r.kept_edited.length} kept because a person edited them`)
      await reload()
    } catch (err) {
      toast.error(apiError(err, 'Could not refresh the generated cases'))
    } finally {
      setBusy(null)
    }
  }

  const nav: TestingNav = { go, runCases, askAi, fixCase }
  const noCases = !overview?.cases

  const runAllBtn = (variant: 'primary' | 'secondary') => (
    <Button variant={variant} icon={<Play size={15} />} loading={busy === 'run'}
            disabled={noCases} onClick={() => void runCases()}>Run all tests</Button>
  )

  const headerActions = !canTest ? null : (
    <>
      {tab === 'overview' && (
        <>
          <Button variant="ghost" icon={<Sparkles size={15} />} onClick={() => askAi()}>Ask AI to write tests</Button>
          {runAllBtn('secondary')}
        </>
      )}
      {tab === 'cases' && (
        <>
          <Button variant="secondary" icon={<RefreshCw size={15} />} loading={busy === 'seed'} onClick={onSeed}>
            Refresh generated cases
          </Button>
          <Button icon={<Sparkles size={15} />} onClick={() => askAi()}>Ask AI to write tests</Button>
        </>
      )}
      {tab === 'runs' && (
        <>
          <Button variant="ghost" icon={<SlidersHorizontal size={15} />} disabled={noCases}
                  onClick={() => setChooseOpen(true)}>Choose what to run…</Button>
          {runAllBtn('secondary')}
        </>
      )}
    </>
  )

  const tabs: { key: TabKey; label: string; count?: number; alert?: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'cases', label: 'Test cases', count: overview?.cases },
    { key: 'runs', label: 'Runs', count: overview?.runs.length },
    { key: 'unit', label: 'Unit tests', alert: unitFailing ? `${unitFailing} failing` : undefined },
    { key: 'bugs', label: 'Bugs', alert: openCount ? `${openCount} need${openCount === 1 ? 's' : ''} you` : undefined },
    { key: 'data', label: 'Test data' },
  ]

  return (
    <div style={{ minHeight: '100%' }}>
      <header style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '16px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <button type="button" onClick={() => navigate('/workbench')} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)',
                fontSize: 14, padding: 0, alignSelf: 'flex-start',
              }}>
                <ArrowLeft size={14} /> {project} · Advanced
              </button>
              <h1 style={{ margin: 0, fontSize: 27, fontWeight: 600, letterSpacing: '-0.01em' }}>Testing</h1>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>{headerActions}</div>
          </div>
          <nav role="tablist" aria-label="Testing sections"
               style={{ display: 'flex', gap: 28, marginTop: 14, overflowX: 'auto' }}>
            {tabs.map(({ key, label, count, alert }) => {
              const on = tab === key
              return (
                <button key={key} type="button" role="tab" aria-selected={on} onClick={() => go(key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 2px', whiteSpace: 'nowrap',
                  fontSize: 15, fontWeight: on ? 600 : 500,
                  color: on ? 'var(--color-text)' : 'var(--color-text-secondary)',
                  borderBottom: `2px solid ${on ? 'var(--color-accent)' : 'transparent'}`,
                }}>
                  {label}
                  {count != null && count > 0 && <TabCount>{count}</TabCount>}
                  {alert && <TabCount alert>{alert}</TabCount>}
                </button>
              )
            })}
          </nav>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 24px 40px' }}>
        {overview && !canTest && (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 16px' }}>
            You can look at the results here. Running tests and deciding on bugs needs the
            <b> Test</b> permission on this project.
          </p>
        )}
        {loading && !overview ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
        ) : (
          <>
            {tab === 'overview' && (
              <OverviewTab project={project} overview={overview} coverage={coverage} latestRun={latestRun}
                           canTest={canTest} nav={nav} onSeed={onSeed} seeding={busy === 'seed'}
                           onReload={reload} />
            )}
            {tab === 'cases' && (
              <CasesTab project={project} canTest={canTest} plan={overview?.plan ?? null} coverage={coverage}
                        lastResults={lastResults} focus={focus} nav={nav} onReload={reload} />
            )}
            {tab === 'runs' && (
              <RunsTab project={project} canTest={canTest} canFix={!!overview?.can_fix} focus={focus} nav={nav} onChanged={reload}
                       onRunAll={() => void runCases()} running={busy === 'run'} noCases={noCases} />
            )}
            {tab === 'unit' && (
              <UnitTestsTab project={project} canTest={canTest} canFix={!!overview?.can_fix} onFix={fixUnit}
                            onChanged={reload} />
            )}
            {tab === 'bugs' && <DefectsTab project={project} canTest={canTest} nav={nav} onChanged={reload} />}
            {tab === 'data' && <TestDataTab project={project} canTest={canTest} onChanged={reload} />}
          </>
        )}
      </main>

      {chooseOpen && (
        <RunModal project={project} personas={Object.keys(overview?.plan?.personas || { owner: {} })}
                  onClose={() => setChooseOpen(false)}
                  onStarted={(id) => { setChooseOpen(false); go('runs', { run: id }); void reload() }} />
      )}
    </div>
  )
}

function TabCount({ children, alert }: { children: React.ReactNode; alert?: boolean }) {
  return (
    <span style={{
      fontSize: 13, fontWeight: 600, lineHeight: 1, padding: '3px 7px', borderRadius: 'var(--radius-full)',
      background: alert ? 'var(--color-error-weak)' : 'var(--color-secondary-btn)',
      color: alert ? 'var(--color-error)' : 'var(--color-text-secondary)',
    }}>{children}</span>
  )
}

/** Runs that are still going — the Overview and Runs tabs poll while one is. */
export const isLive = (status?: string) => !!status && LIVE.has(status)
